import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { Box, Stack, Typography } from "@mui/material"
import { t } from "i18next"
import { FC, useCallback } from "react"
import { OrderViewModel } from "src/db/Order"
import {
  OrderOverlay,
  SortableItem,
} from "src/features/shared-module/components"
import { appConstants } from "src/features/shared-module/constants"
import { useAppDispatch, useAppSelector } from "../hooks"
import {
  selectedOrderIdsSelector,
  toggleSelectOrder,
} from "../slices/dashboard.slice"
import { DropOrderPlaceholder } from "./DropOrderPlaceholder"
import { OrderCard } from "./OrderCard"

export const CommonSortableOrderList: FC<{
  items: OrderViewModel[]
  contextId: string
}> = ({ items, contextId }) => {
  const dispatch = useAppDispatch()
  const activeDragItem = useAppSelector(
    (state) => state.dashboard.activeDragItem,
  )
  const selectedOrderIds = useAppSelector(selectedOrderIdsSelector)
  const isReadyForHaulage = contextId === "READY_FOR_HAULAGE"

  const handleToggleSelect = useCallback(
    (orderId: number, shiftKey: boolean) => {
      dispatch(toggleSelectOrder({ orderId, shiftKey }))
    },
    [dispatch],
  )

  return (
    <>
      <SortableContext
        id={`${contextId}`}
        items={items.map((order) => order.id)}
        strategy={verticalListSortingStrategy}
      >
        <Stack
          spacing={1}
          sx={{
            "& > .MuiBox-root:nth-of-type(1)": {
              "& .MuiDivider-root::before": {
                visibility: "hidden",
              },
            },
            "& > .MuiBox-root:last-child": {
              "& .MuiDivider-root::after": {
                visibility: "hidden",
              },
            },
          }}
        >
          {items.map((order) => {
            return (
              <SortableItem key={order.id} id={order.id}>
                {({ listeners }: any) => (
                  <OrderCard
                    listeners={listeners}
                    showDivider={order?.routeSummaryId ? true : false}
                    order={order}
                    selectable={isReadyForHaulage}
                    isSelected={
                      isReadyForHaulage &&
                      selectedOrderIds.includes(order.id)
                    }
                    onToggleSelect={
                      isReadyForHaulage ? handleToggleSelect : undefined
                    }
                  />
                )}
              </SortableItem>
            )
          })}
        </Stack>
        {items.length === 0 &&
          (activeDragItem ? (
            <SortableItem id={contextId}>
              <DropOrderPlaceholder />
            </SortableItem>
          ) : (
            <Box
              data-testid={`orders-empty-${contextId}`}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: 2,
                borderRadius: 2,
                border: "1.5px dashed",
                height: appConstants.orderRawHeight,
              }}
            >
              <Typography fontWeight={600} color="text.secondary">
                {t("noOrder")}
              </Typography>
            </Box>
          ))}
      </SortableContext>
      <OrderOverlay />
    </>
  )
}
